// "Show on image" switches and an optional layout picker for a server-rendered
// share image — the web counterpart of the mobile share-image builder's
// SHOW ON IMAGE card (ListShareImageScreen), with the same rows: a label, a
// one-line explanation, and a reason when a part can't be shown.
// Rendered inside PosterSharePanel, so the preview above always shows them.
// An optional Footer message picker (shareFooterOptions rows) follows the
// switches, like the mobile builders' FOOTER MESSAGE card.
import styles from '../TopMovies/TopMovies.module.css';
import own from './ShareImageOptions.module.css';

/**
 * @param {{key: string, label: string, sub: string, value: boolean, unavailable?: string, locked?: string}[]} parts
 *        `unavailable`: can't be shown (off, disabled, with the reason);
 *        `locked`: always shown in this layout (on, disabled, with the reason)
 * @param {(key: string, value: boolean) => void} onToggle
 * @param {{value: string, options: {key: string, label: string}[], hint?: string}} [layout]
 * @param {(key: string) => void} [onLayoutChange]
 * @param {string} [layoutLabel]  heading of the layout picker
 * @param {{value: string, options: {key: string, label: string, sub: string}[]}} [footer]
 *        the Footer message choice (see @aluvo/constants/src/shareFooter)
 * @param {(key: string) => void} [onFooterChange]
 */
export default function ShareImageOptions({ parts, onToggle, layout, onLayoutChange, layoutLabel = 'Image layout', footer, onFooterChange }) {
    return (
        <>
            {layout && (
                <div>
                    <p className={styles.sectionLabel}>{layoutLabel}</p>
                    <div className={styles.segmented} role="group" aria-label={layoutLabel}>
                        {layout.options.map((option) => (
                            <button key={option.key} type="button" aria-pressed={layout.value === option.key} onClick={() => onLayoutChange(option.key)}>{option.label}</button>
                        ))}
                    </div>
                    {layout.hint && <p className={styles.hint} style={{ marginTop: 6 }}>{layout.hint}</p>}
                </div>
            )}
            <fieldset className={own.parts}>
                <legend className={styles.sectionLabel}>Show on image</legend>
                {parts.map((part) => (
                    <label key={part.key} className={own.row}>
                        <span className={own.text}>
                            <strong className={part.unavailable ? own.muted : undefined}>{part.label}</strong>
                            <span>{part.unavailable || part.locked || part.sub}</span>
                        </span>
                        <input
                            type="checkbox"
                            role="switch"
                            checked={part.locked ? true : !part.unavailable && part.value}
                            disabled={Boolean(part.unavailable || part.locked)}
                            onChange={(e) => onToggle(part.key, e.target.checked)}
                            aria-label={part.label}
                        />
                    </label>
                ))}
            </fieldset>
            {footer && (
                <fieldset className={own.parts}>
                    <legend className={styles.sectionLabel}>Footer message</legend>
                    {footer.options.map((option) => (
                        <label key={option.key} className={own.row}>
                            <span className={own.text}>
                                <strong>{option.label}</strong>
                                <span>{option.sub}</span>
                            </span>
                            <input
                                type="radio"
                                name="share-footer"
                                value={option.key}
                                checked={footer.value === option.key}
                                onChange={() => onFooterChange(option.key)}
                                aria-label={option.label}
                            />
                        </label>
                    ))}
                </fieldset>
            )}
        </>
    );
}
